import {BaseApi} from './BaseApi'

// Enums from backend
export enum BillcodeStatus {
  Active = 'Active',
  Archived = 'Archived', // Updated from 'Inactive' to match backend
}

export enum BillcodeType {
  MarkUp = 'MARK_UP',
}

export enum USState {
  AL = 'AL',
  AK = 'AK',
  AZ = 'AZ',
  AR = 'AR',
  CA = 'CA',
  CO = 'CO',
  CT = 'CT',
  DE = 'DE',
  FL = 'FL',
  GA = 'GA',
  HI = 'HI',
  ID = 'ID',
  IL = 'IL',
  IN = 'IN',
  IA = 'IA',
  KS = 'KS',
  KY = 'KY',
  LA = 'LA',
  ME = 'ME',
  MD = 'MD',
  MA = 'MA',
  MI = 'MI',
  MN = 'MN',
  MS = 'MS',
  MO = 'MO',
  MT = 'MT',
  NE = 'NE',
  NV = 'NV',
  NH = 'NH',
  NJ = 'NJ',
  NM = 'NM',
  NY = 'NY',
  NC = 'NC',
  ND = 'ND',
  OH = 'OH',
  OK = 'OK',
  OR = 'OR',
  PA = 'PA',
  RI = 'RI',
  SC = 'SC',
  SD = 'SD',
  TN = 'TN',
  TX = 'TX',
  UT = 'UT',
  VT = 'VT',
  VA = 'VA',
  WA = 'WA',
  WV = 'WV',
  WI = 'WI',
  WY = 'WY',
  DC = 'DC',
}

// Request/Response Types
export interface BillcodeRateRequest {
  paycodeId: string
  markupPercentage: number // 0-100
  billablePercentage: number // 0-100
  nonBillablePercentage: number // 0-100
}

export interface BillcodeRateResponse extends BillcodeRateRequest {
  _id: string
  paycodeName?: string // Populated from paycode service
  paycodeSystemCode?: string // Populated from paycode service
}

export interface AutoRenewalSettingsRequest {
  enabled?: boolean
  notificationWeeksBefore?: number
}

export interface AutoRenewalSettingsResponse {
  enabled: boolean
  notificationWeeksBefore: number
}

export interface CreateBillcodeRequest {
  name: string
  type: BillcodeType
  // endDate is now auto-calculated by backend (earliest effective date + 1 year)
  state: USState
  wcCodeId: string
  description?: string
  newHireEffectiveDate: string // ISO date string
  autoRenewalSettings?: AutoRenewalSettingsRequest
  newHireRates: BillcodeRateRequest[]
  hasRolloverRates?: boolean
  rolloverRatesEffectiveDate?: string // ISO date string
  rolloverRates?: BillcodeRateRequest[]
  hasReferralRates?: boolean
  referralRatesEffectiveDate?: string // ISO date string
  referralRates?: BillcodeRateRequest[]
}

export interface UpdateBillcodeRequest {
  _id: string
  name?: string
  type?: BillcodeType
  // endDate is now auto-calculated by backend (earliest effective date + 1 year)
  state?: USState
  wcCodeId?: string
  description?: string
  newHireEffectiveDate?: string // ISO date string
  autoRenewalSettings?: AutoRenewalSettingsRequest
  newHireRates?: BillcodeRateRequest[]
  hasRolloverRates?: boolean
  rolloverRatesEffectiveDate?: string // ISO date string
  rolloverRates?: BillcodeRateRequest[]
  hasReferralRates?: boolean
  referralRatesEffectiveDate?: string // ISO date string
  referralRates?: BillcodeRateRequest[]
}

export interface EndBillcodeRequest {
  _id: string
  endDate: string // ISO date string
}

export interface BillcodeResponse {
  _id: string
  name: string
  type: BillcodeType
  endDate: string // ISO date string
  status: BillcodeStatus
  state: USState
  wcCodeId: string
  wcCode?: string // Populated from WC code service
  wcDescription?: string // Populated from WC code service
  description?: string
  newHireEffectiveDate: string // ISO date string
  autoRenewalSettings: AutoRenewalSettingsResponse
  newHireRates: BillcodeRateResponse[]
  hasRolloverRates: boolean
  rolloverRatesEffectiveDate?: string // ISO date string
  rolloverRates?: BillcodeRateResponse[]
  hasReferralRates: boolean
  referralRatesEffectiveDate?: string // ISO date string
  referralRates?: BillcodeRateResponse[]
  companyId: string
  createdDate: string // ISO date string
  modifiedDate: string // ISO date string
  isAssignedToPosition?: boolean // True if bill code is assigned to at least one position
}

export interface BillcodeNameIdResponse {
  _id: string
  name: string
}

export interface BillcodePagingResponse {
  billcodes: BillcodeResponse[]
  totalCount: number
  currentPage: number
  totalPages: number
  hasNextPage: boolean
  hasPreviousPage: boolean
}

export interface GetBillcodeByStatusRequest {
  status: BillcodeStatus
}

export interface PagingParameters {
  page?: number
  limit?: number
  sortBy?: string
  sortOrder?: 'ASC' | 'DESC'
}

class BillCodeApi extends BaseApi {
  constructor() {
    super()
  }

  /**
   * Get paginated list of billcodes for a company
   */
  async getBillcodes(
    companyId: string,
    pagingParams?: PagingParameters,
  ): Promise<BillcodePagingResponse> {
    const queryParams = new URLSearchParams()
    if (pagingParams?.page) queryParams.append('page', pagingParams.page.toString())
    if (pagingParams?.limit) queryParams.append('limit', pagingParams.limit.toString())
    if (pagingParams?.sortBy) queryParams.append('sortBy', pagingParams.sortBy)
    if (pagingParams?.sortOrder) queryParams.append('sortOrder', pagingParams.sortOrder)

    const url = `billcode/getBillcodes/${companyId}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`
    return this.get<BillcodePagingResponse>(url)
  }

  /**
   * Get all billcodes for a company (non-paginated)
   */
  async getAllBillcodes(companyId: string): Promise<BillcodeResponse[]> {
    return this.get<BillcodeResponse[]>(`billcode/getAllBillcodes/${companyId}`)
  }

  /**
   * Get billcodes for a company by status (Active/Archived)
   * This is the key endpoint for Active/Archive tabs
   * Backend updated: status param is now a query parameter
   */
  async getBillcodesByStatus(
    companyId: string,
    status: BillcodeStatus,
  ): Promise<BillcodeResponse[]> {
    const response = await this.get<any>(
      `billcode/getBillcodesByStatus/${companyId}?status=${status}`,
    )
    const data = response.data
    // Handle case where data might not be an array
    if (!Array.isArray(data)) {
      console.error('getBillcodesByStatus response.data is not an array:', data)
      return []
    }
    // Transform id to _id for consistency with other endpoints
    return data.map((billcode) => ({
      ...billcode,
      _id: billcode._id || billcode.id,
    }))
  }

  /**
   * Get billcode by ID
   */
  async getBillcodeById(billcodeId: string): Promise<BillcodeResponse> {
    const response = await this.get<any>(`billcode/getBillcode/${billcodeId}`)
    const data = response.data
    // Transform id to _id for consistency
    return {
      ...data,
      _id: data._id || data.id,
    }
  }

  /**
   * Get billcode name/ID pairs for a company (for dropdowns)
   */
  async getBillcodeNameIds(companyId: string): Promise<BillcodeNameIdResponse[]> {
    return this.get<BillcodeNameIdResponse[]>(`billcode/getBillcodeNameIds/${companyId}`)
  }

  /**
   * Get billcode names by IDs (bulk lookup)
   */
  async getBillcodeNamesByIds(billcodeIds: string[]): Promise<BillcodeNameIdResponse[]> {
    return this.post<BillcodeNameIdResponse[]>('billcode/getBillcodeNamesByIds', billcodeIds)
  }

  /**
   * Create a new billcode for a company
   */
  async createBillcode(
    companyId: string,
    billcode: CreateBillcodeRequest,
  ): Promise<BillcodeResponse> {
    return this.post<BillcodeResponse>(`billcode/createBillcode/${companyId}`, billcode)
  }

  /**
   * Update an existing billcode
   */
  async updateBillcode(billcode: UpdateBillcodeRequest): Promise<BillcodeResponse> {
    return this.put<BillcodeResponse>('billcode/updateBillcode', billcode)
  }

  /**
   * Archive a billcode (Platform Admin only)
   * Can only archive if no positions are attached
   * NEW ENDPOINT - replaces endBillcode
   */
  async archiveBillcode(billcodeId: string): Promise<BillcodeResponse> {
    return this.put<BillcodeResponse>(`billcode/archiveBillcode/${billcodeId}`, {})
  }

  /**
   * Unarchive a billcode (Platform Admin only)
   * If the billcode end date has expired, it will be extended by 1 year from today
   * NEW ENDPOINT
   */
  async unarchiveBillcode(billcodeId: string): Promise<BillcodeResponse> {
    return this.put<BillcodeResponse>(`billcode/unarchiveBillcode/${billcodeId}`, {})
  }

  /**
   * Delete a billcode (hard delete)
   */
  async deleteBillcode(billcodeId: string): Promise<void> {
    return this.delete(`billcode/deleteBillcode/${billcodeId}`)
  }
}

export const billCodeApi = new BillCodeApi()
